import { useCallback, useMemo } from 'react'

import { useMutation, useQuery } from '@tanstack/react-query'

import { useDispatch } from '@tabeeb/store'
import { useApi } from '@tabeeb/modules/shared/utils/API'
import { onAddErrorNotification } from '@tabeeb/modules/notification/actions'

import { CorporationCreateOrUpdateRequest } from '@tabeeb/contracts/microservices/swagger-tenant-service'
import { CorporationsQueryKeys } from '../queries'
import { Corporation } from '../types'

const useCorporations = ({ search = '' }: { search?: string }) => {
  const dispatch = useDispatch()

  const { tenantServiceApi: api } = useApi()

  const query = useQuery({
    queryKey: [CorporationsQueryKeys.Corporations],
    queryFn: () => api.corporations.getAllAsync(),
  })

  const createMutation = useMutation({
    mutationFn: api.corporations.createAsync,
    onError: (error) => {
      dispatch(onAddErrorNotification({ message: 'Failed to create corporation' }))
    },
    onSuccess: (data, request) => {
      query.refetch()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: api.corporations.deleteAsync,
    onError: (error) => {
      dispatch(onAddErrorNotification({ message: 'Failed to delete corporation' }))
    },
    onSuccess: (data, request) => {
      query.refetch()
    },
  })

  const updateMutation = useMutation({
    mutationFn: (request: CorporationCreateOrUpdateRequest & { Id: number }) =>
      api.corporations.updateAsync(request.Id, request),
    onError: (error) => {
      dispatch(onAddErrorNotification({ message: 'Failed to update corporation' }))
    },
    onSuccess: (data, request) => {
      query.refetch()
    },
  })

  const onAddTenants = useCallback(
    async (corporation: Corporation, tenantIds: number[]) => {
      const existingTenantIds = corporation.Tenants?.map((t) => t.Id) ?? []
      const newTenantIds = [...new Set([...existingTenantIds, ...tenantIds])]

      await updateMutation.mutateAsync({
        Id: corporation.Id,
        Name: corporation.Name,
        Description: corporation.Description ?? '',
        TenantIds: newTenantIds,
        AdminUserIds: corporation.AdminUserIds ?? [],
      })
    },
    [updateMutation]
  )

  const onRemoveTenants = useCallback(
    async (corporation: Corporation, tenantIds: number[]) => {
      const existingTenantIds = corporation.Tenants?.map((t) => t.Id) ?? []
      const newTenantIds = existingTenantIds.filter((id) => !tenantIds.includes(id))

      await updateMutation.mutateAsync({
        ...corporation,
        TenantIds: newTenantIds,
      })
    },
    [updateMutation]
  )

  const onAddAdmins = useCallback(
    async (corporation: Corporation, adminIds: string[]) => {
      const existingAdminIds = corporation.AdminUserIds ?? []
      const newAdminIds = [...new Set([...existingAdminIds, ...adminIds])]

      await updateMutation.mutateAsync({
        Id: corporation.Id,
        Name: corporation.Name,
        Description: corporation.Description ?? '',
        TenantIds: corporation.Tenants?.map((t) => t.Id) ?? [],
        AdminUserIds: newAdminIds,
      })
    },
    [updateMutation]
  )

  const onRemoveAdmins = useCallback(
    async (corporation: Corporation, adminIds: string[]) => {
      const existingAdminIds = corporation.AdminUserIds ?? []
      const newAdminIds = existingAdminIds.filter((id) => !adminIds.includes(id))

      await updateMutation.mutateAsync({
        Id: corporation.Id,
        Name: corporation.Name,
        Description: corporation.Description ?? '',
        TenantIds: corporation.Tenants?.map((t) => t.Id) ?? [],
        AdminUserIds: newAdminIds,
      })
    },
    [updateMutation]
  )

  return useMemo(
    () => ({
      loading: query.isLoading || query.isFetching,
      corporations: (query.data ?? []).filter((c) => c.Name.toLowerCase().includes(search.toLowerCase())),
      onCreate: createMutation.mutateAsync,
      onDelete: deleteMutation.mutateAsync,
      onUpdate: updateMutation.mutateAsync,
      onAddTenants,
      onRemoveTenants,
      onAddAdmins,
      onRemoveAdmins,
      onReload: query.refetch,
    }),
    [
      createMutation.mutateAsync,
      deleteMutation.mutateAsync,
      onAddAdmins,
      onAddTenants,
      onRemoveAdmins,
      onRemoveTenants,
      query.data,
      query.isFetching,
      query.isLoading,
      query.refetch,
      search,
      updateMutation.mutateAsync,
    ]
  )
}

export default useCorporations
